// Data-access layer for the chat surface — supabase provider.
//
// Behavioral twin of src/features/chat/firestore.js. The snapshot semantics
// (full sorted, capped array on every change) are reproduced by
// maintainCollection(); the message shape is produced by the shared
// normalizeMessage() so firebase and supabase return byte-identical rows.
//
// snake_case ⇄ camelCase: the Postgres columns are snake_case (sender_id,
// sender_name, avatar_seed, reply_to, sent_at). rowToData() remaps them to the
// camelCase normalizeMessage expects, AND converts sent_at (an ISO string from
// Postgres) to epoch ms so sortKey() works the same as on the Firestore side.
import { getClient } from './client.js';
import { maintainCollection } from './realtime.js';
import { normalizeMessage, sortKey, newMessageId } from '../shapes.js';

// Hard upper bound on confirmed messages held in memory per room — mirrors the
// Firestore impl's MESSAGES_LIMIT. The renderer paginates further; this just
// keeps a runaway room bounded.
const MESSAGES_LIMIT = 500;

// Design constraint (F5.4): cap distinct emoji per message at 4. Enforced
// client-side only — no RLS gate on this in the schema. Documented weakness,
// identical to the Firestore side.
export const MAX_DISTINCT_REACTIONS = 4;

// Map a Postgres `messages` row (snake_case) to the camelCase field map
// normalizeMessage() consumes. sent_at (ISO timestamptz string) → epoch ms or
// null while a write is in flight.
function rowToData(row) {
  row = row || {};
  return {
    text: row.text,
    senderId: row.sender_id,
    senderName: row.sender_name,
    avatarSeed: row.avatar_seed,
    code: row.code,
    sentAt: row.sent_at != null ? Date.parse(row.sent_at) : null,
    replyTo: row.reply_to ?? null,
    reactions: row.reactions ?? {},
    attachment: row.attachment ?? null,
  };
}

/**
 * Subscribe to messages for a party code. Invokes `cb` with the FULL sorted,
 * capped array on every change (contract §1, §2). Sorting is client-side via
 * sortKey() — no orderBy/limit at the query level, so legacy rows missing the
 * sort field aren't silently dropped. Returns the idempotent unsubscribe fn.
 */
export function subscribeMessages(code, cb) {
  // Cap to the newest MESSAGES_LIMIT rows FROM THE TAIL inside a cb wrapper,
  // mirroring the Firestore slice(-MESSAGES_LIMIT).
  const capped = (rows) =>
    cb(rows.length > MESSAGES_LIMIT ? rows.slice(-MESSAGES_LIMIT) : rows);

  return maintainCollection({
    table: 'messages',
    filter: { column: 'code', value: code },
    mapRow: (row) => normalizeMessage({ id: row.id, data: rowToData(row) }),
    compare: (a, b) => sortKey(a) - sortKey(b),
    cb: capped,
  });
}

/**
 * List the rooms this user has sent a message in (the "rooms I've interacted
 * in" history). Returns one entry per distinct room code with the most-recent
 * message timestamp the user sent there. Mirrors the Firestore provider's
 * listMyRooms, byte-identical return shape.
 *
 * @param {string} userId
 * @returns {Promise<Array<{ code: string, lastActive: number|null }>>}
 */
export async function listMyRooms(userId) {
  if (!userId) return [];
  const { data, error } = await getClient()
    .from('messages')
    .select('code, sent_at')
    .eq('sender_id', userId);
  if (error) {
    throw new Error(`listMyRooms failed: ${error.message}`);
  }
  const byCode = new Map(); // code -> latest sent_at (epoch ms or null)
  (data || []).forEach((row) => {
    const code = row.code;
    if (!code) return;
    const ts = row.sent_at != null ? Date.parse(row.sent_at) : null;
    const valid = typeof ts === 'number' && Number.isFinite(ts) ? ts : null;
    if (!byCode.has(code)) {
      byCode.set(code, valid);
      return;
    }
    const prev = byCode.get(code);
    if (valid != null && (prev == null || valid > prev)) byCode.set(code, valid);
  });
  return [...byCode.entries()].map(([code, lastActive]) => ({ code, lastActive }));
}

// supabase-js returns { error } rather than throwing on a failed write. Wrap
// inserts so failures surface to the caller (ChatRegion's send catch) instead
// of silently vanishing — e.g. the uuid-column rejection that hit when an
// insecure-context client generated a non-UUID id.
async function insertMessage(row) {
  const { error } = await getClient().from('messages').insert(row);
  if (error) {
    throw new Error(`message insert failed: ${error.message}`);
  }
}

/**
 * Persist a new plain-text message. Always writes the new shape; sent_at is
 * omitted so the DB default now() stamps it server-side. The id is a
 * client-side crypto.randomUUID() (newMessageId) to avoid the legacy Date.now()
 * collision risk.
 */
export async function sendMessage({ code, text, senderId, senderName, avatarSeed }) {
  const id = newMessageId();
  await insertMessage({
      id,
      text,
      sender_id: senderId,
      sender_name: senderName,
      avatar_seed: avatarSeed || senderId,
      code,
      reply_to: null,
      reactions: {},
      // sent_at omitted → DB default now()
    });
  return id;
}

/**
 * Send a code-snippet attachment (F12.1). Mirrors the Firestore version's
 * attachment shape exactly: empty `text`, the renderer pulls from attachment.*.
 * `transferId` is a separate UUID from the message id so a retry of the write
 * doesn't change the transferId the receiver sees. `via` snapshots transport.
 */
export async function sendCodeSnippet({
  code,
  lang,
  title,
  text,
  senderId,
  senderName,
  avatarSeed,
  via,
}) {
  const id = newMessageId();
  const transferId =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `tx-${Date.now()}-${Math.random().toString(36).slice(2)}`;

  const attachment = {
    kind: 'code',
    transferId,
    lang: String(lang || ''),
    text: String(text ?? ''),
    via: via === 'lan' ? 'lan' : 'cloud',
    fromId: senderId,
    ts: Date.now(),
  };
  if (title) attachment.title = title;

  await insertMessage({
      id,
      text: '',
      sender_id: senderId,
      sender_name: senderName,
      avatar_seed: avatarSeed || senderId,
      code,
      reply_to: null,
      reactions: {},
      attachment,
    });
  return id;
}

/**
 * Send a file attachment (F11.1 + F11.2). Symmetric to sendCodeSnippet — empty
 * `text`, the attachment carries the payload. The message id and `transferId`
 * are separate UUIDs so a retried write doesn't re-render the receiver's card.
 *
 * S8 extension (supabase-only): when an `attachment` AttachmentRef is supplied
 * (produced by storage.uploadFile for the cloud-share path), its url +
 * storagePath are merged onto the attachment object so the receiving Bubble can
 * render a download link. Absent it, the LAN/Beam shape (transferId/via) is
 * built exactly as the Firestore version does.
 */
export async function sendFileMessage({
  code,
  transferId,
  name,
  size,
  mime,
  via,
  senderId,
  senderName,
  avatarSeed,
  fromId,
  ts,
  attachment: ref,
}) {
  const id = newMessageId();
  const tid =
    transferId ||
    (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `tx-${Date.now()}-${Math.random().toString(36).slice(2)}`);

  const attachment = {
    kind: 'file',
    transferId: tid,
    name: String((ref && ref.name) || name || 'file'),
    size: Number((ref && ref.size) ?? size) || 0,
    mime: String((ref && ref.mime) || mime || 'application/octet-stream'),
    via: via === 'lan' ? 'lan' : 'cloud',
    fromId: fromId || senderId,
    ts: typeof ts === 'number' ? ts : Date.now(),
  };
  // S8 cloud-share fields, only when a stored AttachmentRef is supplied.
  if (ref && ref.url) attachment.url = ref.url;
  if (ref && ref.storagePath) attachment.storagePath = ref.storagePath;

  await insertMessage({
      id,
      text: '',
      sender_id: senderId,
      sender_name: senderName,
      avatar_seed: avatarSeed || senderId,
      code,
      reply_to: null,
      reactions: {},
      attachment,
    });
  return id;
}

/**
 * Toggle a single user's reaction on a message. Read-modify-write the jsonb
 * `reactions` column, replicating the EXACT cap logic from firestore.js:
 *   - If `userId` is already in reactions[emoji], remove them.
 *   - Otherwise add them.
 *   - Adding a NEW distinct emoji at MAX_DISTINCT_REACTIONS distinct emojis is
 *     rejected: returns false, performs NO write. Removal is always allowed.
 * Returns true when a write happened, false when the cap blocked an add.
 *
 * Concurrency note: read-modify-write races on simultaneous reactions, same as
 * the Firestore two-round-trip approach. Acceptable for v1 (reactions aren't a
 * hot path); a normalized (message_id, emoji, user_id) table would remove it.
 */
export async function toggleReaction(messageId, emoji, userId) {
  if (!messageId || !emoji || !userId) return false;
  const supabase = getClient();

  const { data: row, error } = await supabase
    .from('messages')
    .select('reactions')
    .eq('id', messageId)
    .maybeSingle();
  // Missing row / read error → no write, matches the Firestore !exists guard.
  if (error || !row) return false;

  const reactions = row.reactions || {};
  const existing = Array.isArray(reactions[emoji]) ? reactions[emoji] : [];
  const userAlreadyReacted = existing.includes(userId);

  if (!userAlreadyReacted) {
    // Adding — check the cap. Distinct emojis = keys with a non-empty array.
    const distinctActive = Object.entries(reactions).filter(
      ([, arr]) => Array.isArray(arr) && arr.length > 0,
    );
    const alreadyHasThisEmoji = distinctActive.some(([k]) => k === emoji);
    if (
      !alreadyHasThisEmoji &&
      distinctActive.length >= MAX_DISTINCT_REACTIONS
    ) {
      return false;
    }
    const next = { ...reactions, [emoji]: [...existing, userId] };
    await supabase.from('messages').update({ reactions: next }).eq('id', messageId);
    return true;
  }

  // Removing — always allowed, even at cap. Drop the user; prune the emoji key
  // entirely when it empties so the distinct-count stays accurate (the
  // Firestore arrayRemove leaves an empty array, but the cap logic above only
  // counts non-empty arrays, so either representation is equivalent — we prune
  // to keep the jsonb tidy).
  const remaining = existing.filter((u) => u !== userId);
  const next = { ...reactions };
  if (remaining.length > 0) next[emoji] = remaining;
  else delete next[emoji];
  await supabase.from('messages').update({ reactions: next }).eq('id', messageId);
  return true;
}
